import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { AlertTriangle, Shield, Activity, Cpu, Network, Zap, Clock, Award } from 'lucide-react';

// Challenge data from database
const challenges = [
    {
        id: 1,
        title: 'Defend Against Volumetric DDoS Attack',
        description: 'A massive flood of traffic is overwhelming your server bandwidth. Identify the attack type quickly and deploy the right mitigation before the server goes down!',
        attackType: 'volumetric',
        points: 300,
        hints: ['Look at the traffic volume spike', 'Volumetric attacks consume bandwidth'],
        questions: [
            {
                question: 'What type of attack is currently happening?',
                options: ['SQL Injection', 'Volumetric DDoS', 'XSS Attack', 'Brute Force'],
                correct: 1
            },
            {
                question: 'What is the PRIMARY resource being consumed?',
                options: ['CPU', 'Memory', 'Bandwidth', 'Disk I/O'],
                correct: 2
            },
            {
                question: 'Which mitigation is MOST effective?',
                options: ['Rate Limiting', 'Traffic Filtering/Scrubbing', 'Firewall Rules', 'Load Balancer'],
                correct: 1
            }
        ]
    },
    {
        id: 2,
        title: 'Defend Against Protocol DDoS Attack',
        description: 'Attackers are exploiting protocol weaknesses (SYN floods, etc.) to exhaust server resources. Identify and block it before connection tables fill up!',
        attackType: 'protocol',
        points: 300,
        hints: ['Look for half-open connections', 'Protocol attacks target stateful resources'],
        questions: [
            {
                question: 'What protocol weakness is being exploited?',
                options: ['HTTP Slowloris', 'SYN Flood', 'UDP Flood', 'DNS Amplification'],
                correct: 1
            },
            {
                question: 'What resource is being exhausted?',
                options: ['Bandwidth', 'Connection State Tables', 'Application Memory', 'Database Connections'],
                correct: 1
            },
            {
                question: 'Best mitigation strategy?',
                options: ['Block all traffic', 'SYN Cookies/TCP Hardening', 'Increase bandwidth', 'Restart server'],
                correct: 1
            }
        ]
    },
    {
        id: 3,
        title: 'Defend Against Application Layer DDoS Attack',
        description: 'Slow, targeted HTTP requests are exhausting application resources. Detect the subtle attack and apply precise mitigation.',
        attackType: 'application',
        points: 400,
        hints: ['Traffic volume may not be huge', 'Look at request patterns and response times'],
        questions: [
            {
                question: 'What characterizes this attack?',
                options: ['High bandwidth usage', 'Legitimate-looking slow requests', 'Port scanning', 'Malformed packets'],
                correct: 1
            },
            {
                question: 'Which metric shows the clearest sign?',
                options: ['Bandwidth spike', 'Response time increase', 'Network errors', 'Packet loss'],
                correct: 1
            },
            {
                question: 'Most effective defense?',
                options: ['Block all HTTP', 'Web Application Firewall (WAF)', 'Increase bandwidth', 'Close ports'],
                correct: 1
            }
        ]
    }
];

const DDoSDefenseSimulator = () => {
    const [selectedChallenge, setSelectedChallenge] = useState(0);
    const [isAttackActive, setIsAttackActive] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [serverHealth, setServerHealth] = useState(100);
    const [availabilityData, setAvailabilityData] = useState([]);
    const [trafficData, setTrafficData] = useState([]);
    const [cpuData, setCpuData] = useState([]);
    const [answered, setAnswered] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [gameState, setGameState] = useState('ready'); // ready, playing, won, lost

    const challenge = challenges[selectedChallenge];

    // Attack intensity based on type
    const getAttackParams = (type) => {
        switch (type) {
            case 'volumetric':
                return { trafficMultiplier: 15, cpuImpact: 0.6, healthDrain: 3 };
            case 'protocol':
                return { trafficMultiplier: 8, cpuImpact: 1.2, healthDrain: 2.5 };
            case 'application':
                return { trafficMultiplier: 3, cpuImpact: 1.5, healthDrain: 2 };
            default:
                return { trafficMultiplier: 10, cpuImpact: 1, healthDrain: 2 };
        }
    };

    useEffect(() => {
        if (isAttackActive && gameState === 'playing') {
            const interval = setInterval(() => {
                setTimeElapsed(prev => prev + 1);

                const params = getAttackParams(challenge.attackType);
                const baseTraffic = 100;
                const attackTraffic = baseTraffic * params.trafficMultiplier;
                const baseCpu = 30;
                const attackCpu = Math.min(95, baseCpu + (timeElapsed * params.cpuImpact));

                const newHealth = Math.max(0, serverHealth - params.healthDrain);
                setServerHealth(newHealth);

                if (newHealth <= 0) {
                    setGameState('lost');
                    setIsAttackActive(false);
                }

                setAvailabilityData(prev => {
                    const newData = [...prev, {
                        time: timeElapsed,
                        availability: newHealth,
                        threshold: 70
                    }].slice(-20);
                    return newData;
                });

                setTrafficData(prev => {
                    const newData = [...prev, {
                        time: timeElapsed,
                        normal: baseTraffic,
                        attack: attackTraffic,
                        total: baseTraffic + attackTraffic
                    }].slice(-20);
                    return newData;
                });

                setCpuData(prev => {
                    const newData = [...prev, {
                        time: timeElapsed,
                        cpu: attackCpu,
                        memory: Math.min(90, 40 + timeElapsed * 0.8)
                    }].slice(-20);
                    return newData;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [isAttackActive, timeElapsed, serverHealth, challenge.attackType, gameState]);

    const startAttack = () => {
        setIsAttackActive(true);
        setGameState('playing');
        setTimeElapsed(0);
        setServerHealth(100);
        setCurrentQuestion(0);
        setScore(0);
        setAvailabilityData([]);
        setTrafficData([]);
        setCpuData([]);
        setAnswered(false);
        setSelectedAnswer(null);
        setShowHint(false);
    };

    const handleAnswer = (index) => {
        if (answered) return;

        setSelectedAnswer(index);
        setAnswered(true);

        if (index === challenge.questions[currentQuestion].correct) {
            setScore(prev => prev + 100);

            setTimeout(() => {
                if (currentQuestion < challenge.questions.length - 1) {
                    setCurrentQuestion(prev => prev + 1);
                    setAnswered(false);
                    setSelectedAnswer(null);
                    setShowHint(false);
                } else {
                    setGameState('won');
                    setIsAttackActive(false);
                }
            }, 1500);
        } else {
            setServerHealth(prev => Math.max(0, prev - 15));
        }
    };

    const getHealthColor = (health) => {
        if (health > 70) return 'text-green-500';
        if (health > 40) return 'text-yellow-500';
        return 'text-red-500';
    };

    const getHealthBg = (health) => {
        if (health > 70) return 'bg-green-500';
        if (health > 40) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <div className="min-h-[100dvh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-4 sm:p-6">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Shield className="w-8 h-8 text-blue-400" />
                        <h1 className="text-3xl font-bold">DDoS Defense Simulator</h1>
                    </div>
                    <div className="flex gap-4 items-center">
                        <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
                            <Clock className="w-5 h-5 text-blue-400" />
                            <span className="font-mono">{timeElapsed}s</span>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
                            <Award className="w-5 h-5 text-yellow-400" />
                            <span className="font-mono">{score} pts</span>
                        </div>
                    </div>
                </div>

                {/* Challenge Selector */}
                <div className="flex gap-3 mb-6">
                    {challenges.map((c, idx) => (
                        <button
                            key={c.id}
                            onClick={() => {
                                setSelectedChallenge(idx);
                                setGameState('ready');
                                setIsAttackActive(false);
                            }}
                            className={`flex-1 p-4 rounded-lg border-2 transition-all ${selectedChallenge === idx
                                    ? 'border-blue-500 bg-blue-500/20'
                                    : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                                }`}
                        >
                            <div className="font-semibold text-sm mb-1">{c.title.replace('Defend Against ', '')}</div>
                            <div className="text-xs text-gray-400">{c.points} points</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto grid grid-cols-3 gap-6">
                {/* Left Panel - Metrics */}
                <div className="col-span-2 space-y-4">
                    {/* Server Health Alert */}
                    <div className={`p-4 rounded-lg border-2 ${serverHealth > 70 ? 'border-green-500/30 bg-green-500/10' :
                            serverHealth > 40 ? 'border-yellow-500/30 bg-yellow-500/10' :
                                'border-red-500/30 bg-red-500/10 animate-pulse'
                        }`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className={`w-5 h-5 ${getHealthColor(serverHealth)}`} />
                                <span className="font-semibold">Server Health</span>
                            </div>
                            <span className={`text-2xl font-bold ${getHealthColor(serverHealth)}`}>
                                {serverHealth.toFixed(0)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${getHealthBg(serverHealth)}`}
                                style={{ width: `${serverHealth}%` }}
                            />
                        </div>
                    </div>

                    {/* Availability Chart */}
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                            <Activity className="w-5 h-5 text-blue-400" />
                            <h3 className="font-semibold">Server Availability</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                            <AreaChart data={availabilityData}>
                                <defs>
                                    <linearGradient id="availGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="time" stroke="#9ca3af" />
                                <YAxis stroke="#9ca3af" domain={[0, 100]} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                                />
                                <Area type="monotone" dataKey="availability" stroke="#3b82f6" fillOpacity={1} fill="url(#availGradient)" />
                                <Line type="monotone" dataKey="threshold" stroke="#ef4444" strokeDasharray="5 5" dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Traffic Chart */}
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                            <Network className="w-5 h-5 text-purple-400" />
                            <h3 className="font-semibold">Network Traffic (Mbps)</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={trafficData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="time" stroke="#9ca3af" />
                                <YAxis stroke="#9ca3af" />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                                />
                                <Line type="monotone" dataKey="normal" stroke="#10b981" strokeWidth={2} dot={false} name="Normal" />
                                <Line type="monotone" dataKey="attack" stroke="#ef4444" strokeWidth={2} dot={false} name="Attack" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* CPU/Memory Chart */}
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2 mb-3">
                            <Cpu className="w-5 h-5 text-orange-400" />
                            <h3 className="font-semibold">System Resources (%)</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={cpuData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="time" stroke="#9ca3af" />
                                <YAxis stroke="#9ca3af" domain={[0, 100]} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                                />
                                <Line type="monotone" dataKey="cpu" stroke="#f59e0b" strokeWidth={2} dot={false} name="CPU" />
                                <Line type="monotone" dataKey="memory" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Memory" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Right Panel - Q&A */}
                <div className="space-y-4">
                    {/* Challenge Info */}
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <h3 className="font-bold text-lg mb-2">{challenge.title}</h3>
                        <p className="text-sm text-gray-300 mb-3">{challenge.description}</p>

                        {gameState === 'ready' && (
                            <button
                                onClick={startAttack}
                                className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
                            >
                                <Zap className="w-5 h-5" />
                                START ATTACK
                            </button>
                        )}

                        {gameState === 'won' && (
                            <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 text-center">
                                <h3 className="text-xl font-bold text-green-400 mb-2">🛡️ ATTACK DEFENDED!</h3>
                                <p className="text-sm mb-3">Score: {score} points</p>
                                <button
                                    onClick={startAttack}
                                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {gameState === 'lost' && (
                            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 text-center">
                                <h3 className="text-xl font-bold text-red-400 mb-2">💥 SERVER DOWN!</h3>
                                <p className="text-sm mb-3">Better luck next time!</p>
                                <button
                                    onClick={startAttack}
                                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg"
                                >
                                    Retry Challenge
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Questions */}
                    {gameState === 'playing' && (
                        <div className="bg-gray-800 p-4 rounded-lg border-2 border-yellow-500/50 animate-pulse-slow">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-bold">Question {currentQuestion + 1}/{challenge.questions.length}</h3>
                                <button
                                    onClick={() => setShowHint(!showHint)}
                                    className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                    {showHint ? 'Hide' : 'Show'} Hint
                                </button>
                            </div>

                            {showHint && (
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mb-3 text-xs text-blue-300">
                                    💡 {challenge.hints[currentQuestion % challenge.hints.length]}
                                </div>
                            )}

                            <p className="text-sm mb-4 font-semibold">
                                {challenge.questions[currentQuestion].question}
                            </p>

                            <div className="space-y-2">
                                {challenge.questions[currentQuestion].options.map((option, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAnswer(idx)}
                                        disabled={answered}
                                        className={`w-full p-3 rounded-lg text-left text-sm transition-all border-2 ${answered && idx === challenge.questions[currentQuestion].correct
                                                ? 'border-green-500 bg-green-500/20'
                                                : answered && idx === selectedAnswer
                                                    ? 'border-red-500 bg-red-500/20'
                                                    : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                                            } ${answered ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Progress */}
                    {gameState === 'playing' && (
                        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                            <h3 className="font-semibold mb-3 text-sm">Progress</h3>
                            <div className="flex gap-2">
                                {challenge.questions.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex-1 h-2 rounded-full ${idx < currentQuestion ? 'bg-green-500' :
                                                idx === currentQuestion ? 'bg-yellow-500 animate-pulse' :
                                                    'bg-gray-600'
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DDoSDefenseSimulator;